import { InlayPanel, Separator } from '@sorrem-client/ui/components';
import { formatRecommendedReadiness, resolveVeilSaturationById } from '@sorrem/shared';
import { resolveSaturationAccent } from '@sorrem-client/ui/features/CharacterHome/hooks';
import type { CrucibleCreateDraft } from '../CreateCruciblePanel';
import styles from './CrucibleInfoPanel.module.scss';
import { toCrucibleAccentStyle } from '@sorrem-client/ui/features/CharacterHome/crucible-links';

/** Banner tint while the saturation is still to be rolled. */
const UNROLLED_ACCENT = '#3a3f52';

interface CrucibleCreatePreviewProps {
    readonly draft: CrucibleCreateDraft;
}

/**
 * Crucible Info while the create form is open (#224): what the form would
 * make right now. Its size (rooms, a new map) or authored crucible, how many
 * can join, and its saturation's modifiers, or that it will be rolled.
 */
export function CrucibleCreatePreview({ draft }: CrucibleCreatePreviewProps): JSX.Element {
    // A random crucible's roll (#225) already knows a saturation left to chance.
    const isRolled = draft.saturation === 'random';
    const bandId = isRolled ? draft.flavor?.saturation : draft.saturation;
    const band = bandId ? resolveVeilSaturationById(bandId) : null;
    const choiceName = draft.kind === 'random' ? draft.recipe?.name : draft.template?.displayName;
    const name = draft.name.trim() || draft.flavor?.name || choiceName || 'New Crucible';

    return (
        <>
            <InlayPanel
                className={styles.banner}
                style={toCrucibleAccentStyle(band ? resolveSaturationAccent(band.id) : UNROLLED_ACCENT)}
            >
                <p className={styles.crucibleName}>{name}</p>
                <p className={styles.crucibleTier}>
                    {`${draft.kind === 'random' ? 'Random' : 'Authored'} - ${band ? `${band.displayName}${isRolled ? ' (rolled)' : ''}` : 'Saturation to be rolled'}`}
                </p>
            </InlayPanel>

            {draft.flavor !== null && draft.flavor.backstory.length > 0 && <p className={styles.backstory}>{draft.flavor.backstory}</p>}
            {draft.kind === 'random' && draft.recipe !== null && <p className={styles.description}>{draft.recipe.description}</p>}

            <Separator />

            <section className={styles.section}>
                <h3 className={styles.subheading}>The Crucible</h3>
                <ul className={styles.factList}>
                    {draft.kind === 'random' && draft.recipe !== null && (
                        <>
                            <li>{`About ${draft.recipe.rooms.min}-${draft.recipe.rooms.max} rooms.`}</li>
                            <li>A new map is made for this crucible.</li>
                        </>
                    )}
                    {draft.kind === 'authored' && <li>A hand-made map, the same every time.</li>}
                    <li>
                        {draft.isSolo
                            ? 'Solo run: unlisted, and nobody else can join.'
                            : `Up to ${draft.maxPlayers} players. It starts once everyone gathered is ready.`}
                    </li>
                </ul>
            </section>

            <Separator />

            <section className={styles.section}>
                <h3 className={styles.subheading}>Saturation</h3>
                {band === null ? (
                    <p className={styles.description}>Rolled when the crucible is made; fainter saturations are likelier.</p>
                ) : (
                    <>
                        <p className={styles.description}>{`${band.displayName} - recommended readiness ${formatRecommendedReadiness(band.recommendedReadiness)}`}</p>
                        <ul className={styles.factList}>
                            {band.modifiers.map((modifier) => <li key={modifier}>{modifier}</li>)}
                        </ul>
                    </>
                )}
            </section>
        </>
    );
}
